import { useTrainingLocalization } from "../../../trainingLocalization/TrainingLocalizationHooks";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectCurrentLanguage } from "../../../../app/slices/TrainingLocalizationSlice";
import { selectSequenceVariables } from "../../../../app/slices/TrainingSlice";
import { useRef } from "react";
import classNames from "classnames";
import VariableSuggestionPopper from "../../../variables/VariableSuggestionPopper";
import VariableTextOverlay from "../../../variables/VariableTextOverlay";
import useVariableTextareaController from "../../../variables/useVariableTextareaController.hook";

export default function PiBasicQuizAnswerTextfieldV2(props: {
  localizationKey: string;
  color?: "interaction-blue" | "white";
}) {
  const { localizationKey, color } = { ...props };
  const { getValue, changeValue } = useTrainingLocalization();
  const { t } = useTranslation();
  const currLang = useSelector(selectCurrentLanguage);
  const variables = useSelector(selectSequenceVariables);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const isWhite = color === "white";

  const {
    textareaRef,
    inputValue,
    highlightedSegments,
    scrollTop,
    scrollLeft,
    handleFocus,
    handleChange,
    handleBlur,
    handleClick,
    handleKeyUp,
    handleScroll,
    handleWheel,
    handleKeyDown,
    flushPendingValue,
    suggestions,
  } = useVariableTextareaController({
    value: getValue(localizationKey),
    onCommit: (nextValue) =>
      changeValue(localizationKey, nextValue, currLang.shortcode),
    variables,
    debounceMs: 500,
    flushOnCleanupKeys: [currLang.shortcode, localizationKey],
  });

  // Overlay and textarea must share identical font metrics so the colored
  // overlay text sits exactly over the (transparent) textarea text.
  const sharedMetrics = classNames(
    "whitespace-pre text-[14px] font-medium leading-[20px]",
    { "text-center": isWhite }
  );

  return (
    <div className={"flex w-full h-full"}>
      <div
        ref={inputWrapperRef}
        className={"relative w-full my-auto h-[20px]"}
      >
        <VariableTextOverlay
          segments={highlightedSegments}
          containerClassName="pointer-events-none absolute inset-0 overflow-hidden"
          contentClassName={sharedMetrics}
          textClassName={isWhite ? "text-white" : "text-interaction-primary"}
          knownTokenClassName={
            isWhite
              ? "text-interaction-secondary-faded"
              : "text-interaction-secondary"
          }
          unknownTokenClassName={
            isWhite ? "text-warning-600" : "text-creator-error"
          }
          scrollTop={scrollTop}
          scrollLeft={scrollLeft}
        />
        <textarea
          ref={textareaRef}
          rows={1}
          className={classNames(
            "relative z-[1] block w-full h-full resize-none overflow-hidden bg-transparent outline-none text-transparent placeholder:text-[14px] placeholder:transition-opacity placeholder:duration-100 focus:placeholder:opacity-0",
            sharedMetrics,
            isWhite
              ? "caret-white placeholder:text-white/60"
              : "caret-interaction-primary placeholder:text-interaction-text-placeholder"
          )}
          value={inputValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onClick={handleClick}
          onKeyUp={handleKeyUp}
          onScroll={handleScroll}
          onWheel={handleWheel}
          onKeyDown={(e) => {
            if (e.key === "Enter" && suggestions.open) {
              handleKeyDown(e);
              return;
            }

            if (e.key === "Enter") {
              e.preventDefault();
              flushPendingValue();
              e.currentTarget.blur();
              return;
            }

            handleKeyDown(e);
          }}
          placeholder={t("answer_placeholder") ?? ""}
        />
      </div>

      {suggestions.open && (
        <VariableSuggestionPopper
          open={true}
          anchorEl={suggestions.anchorEl ?? inputWrapperRef.current}
          width={Math.max(inputWrapperRef.current?.clientWidth ?? 0, 240)}
          maxWidth={360}
          suggestions={suggestions.filteredSuggestions}
          selectedIndex={suggestions.selectedIndex}
          menuRef={suggestions.menuRef}
          onSelect={suggestions.onSelect}
        />
      )}
    </div>
  );
}
